import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { io, Socket } from 'socket.io-client';
import helpers from 'helpers/index'; // Đảm bảo helpers này có sẵn

// Định nghĩa kiểu dữ liệu cho context
interface ISocketContext {
  socket: Socket | null;
}

// Tạo Context
const SocketContext = createContext<ISocketContext | undefined>(undefined);

interface SocketProviderProps {
  children: ReactNode;
}

// Hàm khởi tạo socket
const initializeSocket = (url: string): Socket => {
  const socketInstance = io(url + '/socket', {
    withCredentials: true,
    timeout: 60000,
    extraHeaders: {
      'X-Authorization': helpers.cookie_get('AT'), // Lấy token từ cookie
    },
    // Tùy chọn: Tăng thời gian chờ reconnect (nếu cần)
    reconnectionDelayMax: 10000,
  });

  /** Server không tự connect, phải phát connection */
  socketInstance.emit('connection', '');
  console.log('Socket initialized and attempting connection...');
  return socketInstance;
};

export const SocketProvider: React.FC<SocketProviderProps> = ({ children }) => {
  const [socket, setSocket] = useState<Socket | null>(null);

  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    let currentSocket: Socket | null = null;

    // 1. Logic Kết nối/Ngắt kết nối dựa trên visibility
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Tab đang hoạt động (focus)
        if (!currentSocket || !currentSocket.connected) {
          console.log('Tab visible: Reconnecting socket...');
          currentSocket = initializeSocket(backendUrl as string);
          setSocket(currentSocket);
        }
      } else {
        // Tab ở chế độ nền (unfocus)
        if (currentSocket) {
          console.log('Tab hidden: Disconnecting socket...');
          currentSocket.disconnect();
          currentSocket = null;
          setSocket(null);
        }
      }
    };

    // 2. Kết nối ban đầu (khi component mount)
    // Chỉ kết nối nếu tab đang visible ngay từ đầu
    if (document.visibilityState === 'visible') {
      currentSocket = initializeSocket(backendUrl as string);
      setSocket(currentSocket);
    }

    // 3. Lắng nghe sự kiện thay đổi visibility
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // 4. Hủy lắng nghe và ngắt kết nối cuối cùng khi component unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (currentSocket) {
        currentSocket.disconnect();
        console.log('Socket fully disconnected on unmount');
      }
    };
  }, []);

  return <SocketContext.Provider value={{ socket }}>{children}</SocketContext.Provider>;
};

// Custom hook để sử dụng socket (giữ nguyên)
export const useSocket = (): ISocketContext => {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  return context;
};

